/*************************
 * @license
 * Copyright 2024 Myenergi Ltd. All rights reserved.
 * No part of this work may be reproduced, stored in a retrieval system of any nature, or transmitted, in any form or by any means without the prior written permission of Myenergi Ltd., the copyright owner.
 * If any unauthorised acts are carried out in relation to this copyright work, a civil claim for damages may be made and/or a criminal prosecution may result.
 *************************/
import { ApolloClient } from "@apollo/client";
import { create } from "mobx-persist";
import { MobXProviderContext } from "mobx-react";
import { useContext } from "react";
import { AccessGroupStore } from "./AccessGroupStore";
import { AccessManagementStore } from "./AccessManagementStore";
import { AuthStore } from "./AuthStore";
import ColorStore from "./ColorStore";
import { ContextStore } from "./ContextStore";
import { CustomersStore } from "./CustomersStore";
import { DsrSearchStore } from "./DsrSearchStore";
import { HubStore } from "./HubStore";
import { InstallersStore } from "./InstallersStore";
import { NotificationStore } from "./NotificationStore";
import { UsersStore } from "./UsersStore";

const contextStore = new ContextStore();
const authStore = new AuthStore();
const hubStore = new HubStore();
const colorStore = new ColorStore();
const notificationStore = new NotificationStore();
const customersStore = new CustomersStore(authStore, notificationStore);
const dsrSearchStore = new DsrSearchStore();
const installersStore = new InstallersStore(notificationStore);
const usersStore = new UsersStore();
const accessManagementStore = new AccessManagementStore(
  authStore,
  notificationStore,
);
const accessGroupStore = new AccessGroupStore(authStore, notificationStore);

interface StoreObject {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [index: string]: any;
}

const stores = {
  contextStore,
  authStore,
  hubStore,
  colorStore,
  notificationStore,
  customersStore,
  dsrSearchStore,
  installersStore,
  usersStore,
  accessManagementStore,
  accessGroupStore,
};

const hydrate = create({
  storage: localStorage,
  jsonify: true,
});

export const hydrateStores = async () => {
  await hydrate("contextStore", contextStore);
  await hydrate("authStore", authStore);
  await hydrate("hubStore", hubStore);
  await hydrate("colorStore", colorStore);
  await hydrate("notificationStore", notificationStore);
  await hydrate("customersStore", customersStore);
  await hydrate("dsrSearchStore", dsrSearchStore);
  await hydrate("installersStore", installersStore);
  await hydrate("usersStore", usersStore);
  await hydrate("accessManagementStore", accessManagementStore);
  await hydrate("accessGroupStore", accessGroupStore);
};

export function createStores(client: ApolloClient<unknown>) {
  Object.keys(stores).forEach((storeKey) => {
    (stores as StoreObject)[storeKey].client = client;
  });

  return stores;
}

export type Stores = ReturnType<typeof createStores>;

export function useStores() {
  // MobXProviderContext is created by the internals of MobX with the type
  // of <IValueMap> which is a key/value pair type. We can't find a way of
  // telling mobx to use our custom data type, so the only way to
  // force it is to cast it after creation of MobXProviderContext.
  return useContext(MobXProviderContext) as Stores;
}
